import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import DataTable from "react-data-table-component";
import axios from "axios";
import { Spinner, Badge, Button, Card, Row, Col, Dropdown } from "react-bootstrap";
import { toast, ToastContainer } from "react-toastify";
import {
    FiRefreshCw, FiX, FiSearch, FiCreditCard, FiUsers, FiClock, FiCheckCircle, FiAlertCircle, FiCalendar, FiAlertTriangle, FiTrendingUp, FiDownload, FiPhone, FiAlertOctagon, FiEye
} from "react-icons/fi";
import "react-toastify/dist/ReactToastify.css";
import moment from "moment-timezone";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import jsPDF from "jspdf";

const Collection = () => {
    const [reportsData, setReportsData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [activeFilter, setActiveFilter] = useState(null);
    const fetchingRef = useRef(false);

    const calculateOverdueDays = (customer) => {
        if (!customer.payment_date) return 0;

        const today = moment().tz("Asia/Singapore").startOf('day');
        const customerDueDate = moment(customer.payment_date).tz("Asia/Singapore");
        const customerDueDay = customerDueDate.date();

        const pendingAndUnpaidReports = customer.reports?.filter(r =>
            r.status === "Pending" || r.status === "Unpaid"
        ) || [];

        if (pendingAndUnpaidReports.length === 0) return 0;

        const sortedPendingReports = pendingAndUnpaidReports.sort((a, b) =>
            moment(a.month, "MMM YYYY").tz("Asia/Singapore").diff(moment(b.month, "MMM YYYY").tz("Asia/Singapore"))
        );

        for (const report of sortedPendingReports) {
            const pendingMonth = moment(report.month, "MMM YYYY").tz("Asia/Singapore");
            const dueDateForPendingMonth = pendingMonth.date(customerDueDay).startOf('day');

            if (today.isAfter(dueDateForPendingMonth, 'day')) {
                return today.diff(dueDateForPendingMonth, 'days');
            }
        }

        return 0;
    };

    const categorizeOverdueDays = (days) => {
        if (days === 0) return "Current";
        if (days <= 30) return "1-30 Days";
        if (days <= 60) return "31-60 Days";
        if (days <= 90) return "61-90 Days";
        return "90+ Days";
    };

    const calculateEMIDetails = (row) => {
        const billAmount = row.bill_amount || 0;
        const emiPlan = row.emi_plan_duration || 1;
        const reports = row.reports || [];

        const monthlyEMI = billAmount / emiPlan;
        const paidMonths = reports.filter(r => r.status === "Paid").length;
        const paidAmount = paidMonths * monthlyEMI;
        const pendingAmount = billAmount - paidAmount;

        return {
            monthlyEMI,
            paidAmount,
            pendingAmount,
            paidMonths,
            totalMonths: emiPlan
        };
    };

    const filteredReports = useMemo(() => {
        let filtered = reportsData;

        if (activeFilter) {
            filtered = filtered.filter(customer => {
                const overdueDays = calculateOverdueDays(customer);
                const bucket = categorizeOverdueDays(overdueDays);

                switch (activeFilter.type) {
                    case 'overdue-bucket':
                        return bucket === activeFilter.value;
                    case 'current':
                        return overdueDays === 0;
                    case 'overdue':
                        return overdueDays > 0;
                    case 'today-due':
                        const today = moment().tz("Asia/Singapore").startOf('day');
                        const customerDueDate = moment(customer.payment_date).tz("Asia/Singapore");
                        const customerDueDay = customerDueDate.date();
                        const isDueToday = today.date() === customerDueDay;
                        const currentMonthYear = moment().tz("Asia/Singapore").format('MMM YYYY');
                        const isPaidThisMonth = customer.reports?.some(
                            r => r.month === currentMonthYear && r.status === "Paid"
                        );
                        return isDueToday && !isPaidThisMonth;
                    case 'today-paid':
                        const todayForPaid = moment().tz("Asia/Singapore").startOf('day');
                        const customerDueDateForPaid = moment(customer.payment_date).tz("Asia/Singapore");
                        const customerDueDayForPaid = customerDueDateForPaid.date();
                        const isDueTodayForPaid = todayForPaid.date() === customerDueDayForPaid;
                        const currentMonthYearForPaid = moment().tz("Asia/Singapore").format('MMM YYYY');
                        const isPaidThisMonthForPaid = customer.reports?.some(
                            r => r.month === currentMonthYearForPaid && r.status === "Paid"
                        );
                        return isDueTodayForPaid && isPaidThisMonthForPaid;
                    case 'paid-amount':
                        const { paidAmount } = calculateEMIDetails(customer);
                        return paidAmount > 0;
                    case 'pending-amount':
                        const { pendingAmount } = calculateEMIDetails(customer);
                        return pendingAmount > 0;
                    default:
                        return true;
                }
            });
        }

        if (searchTerm) {
            filtered = filtered.filter(
                (r) =>
                    r.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    (r.product_id && r.product_id.toLowerCase().includes(searchTerm.toLowerCase())) ||
                    (r.boDDARefNo && r.boDDARefNo.toLowerCase().includes(searchTerm.toLowerCase())) ||
                    (r.phone_number && r.phone_number.toLowerCase().includes(searchTerm.toLowerCase()))
            );
        }

        return filtered;
    }, [reportsData, searchTerm, activeFilter]);

    const collectionStats = useMemo(() => {
        const stats = {
            totalCustomers: 0,
            totalOverdueAmount: 0,
            overdueBuckets: {
                "1-30 Days": { count: 0, amount: 0 },
                "31-60 Days": { count: 0, amount: 0 },
                "61-90 Days": { count: 0, amount: 0 },
                "90+ Days": { count: 0, amount: 0 },
                "Current": { count: 0, amount: 0 }
            },
            totalPendingAmount: 0
        };

        reportsData.forEach(customer => {
            const overdueDays = calculateOverdueDays(customer);
            const bucket = categorizeOverdueDays(overdueDays);
            const { monthlyEMI, pendingAmount } = calculateEMIDetails(customer);

            stats.totalCustomers++;
            stats.totalPendingAmount += pendingAmount;

            if (overdueDays > 0) {
                stats.totalOverdueAmount += monthlyEMI;
                stats.overdueBuckets[bucket].count++;
                stats.overdueBuckets[bucket].amount += monthlyEMI;
            } else {
                stats.overdueBuckets[bucket].count++;
                stats.overdueBuckets[bucket].amount += monthlyEMI;
            }
        });

        return stats;
    }, [reportsData]);

    const financialStats = useMemo(() => {
        const stats = {
            totalPendingAmount: 0,
            totalPaidAmount: 0,
            todayMonthlyEMI: 0,
            totalBillAmount: 0,
            totalMonthlyEMI: 0,
            todayDueCustomers: 0,
            todayPaidCustomers: 0,
            todayPaidAmount: 0
        };

        const today = moment().tz("Asia/Singapore").startOf('day');
        const currentMonthYear = moment().tz("Asia/Singapore").format('MMM YYYY');

        reportsData.forEach(customer => {
            const billAmount = parseFloat(customer.bill_amount) || 0;
            const emiPlan = parseInt(customer.emi_plan_duration) || 1;
            const reports = customer.reports || [];

            const monthlyEMI = billAmount / emiPlan;
            const paidMonths = reports.filter(r => r.status === "Paid").length;
            const paidAmount = paidMonths * monthlyEMI;
            const pendingAmount = billAmount - paidAmount;

            stats.totalPendingAmount += pendingAmount;
            stats.totalPaidAmount += paidAmount;
            stats.totalBillAmount += billAmount;
            stats.totalMonthlyEMI += monthlyEMI;

            if (customer.payment_date) {
                const customerDueDate = moment(customer.payment_date).tz("Asia/Singapore");
                const customerDueDay = customerDueDate.date();

                const isDueToday = today.date() === customerDueDay;

                if (isDueToday) {
                    const isPaidThisMonth = reports.some(
                        r => r.month === currentMonthYear && r.status === "Paid"
                    );

                    if (!isPaidThisMonth) {
                        stats.todayMonthlyEMI += monthlyEMI;
                        stats.todayDueCustomers += 1;
                    } else {
                        stats.todayPaidCustomers += 1;
                        stats.todayPaidAmount += monthlyEMI;
                    }
                }
            }
        });

        stats.totalPendingAmount = Math.round(stats.totalPendingAmount * 100) / 100;
        stats.totalPaidAmount = Math.round(stats.totalPaidAmount * 100) / 100;
        stats.todayMonthlyEMI = Math.round(stats.todayMonthlyEMI * 100) / 100;
        stats.todayPaidAmount = Math.round(stats.todayPaidAmount * 100) / 100;
        stats.totalBillAmount = Math.round(stats.totalBillAmount * 100) / 100;
        stats.totalMonthlyEMI = Math.round(stats.totalMonthlyEMI * 100) / 100;

        return stats;
    }, [reportsData]);

    const fetchReports = useCallback(async () => {
        if (fetchingRef.current) return;
        fetchingRef.current = true;
        try {
            setLoading(true);
            const res = await axios.get("https://www.azzenterprises.com/api/customers/getreports");
            setReportsData(res.data.allReports || []);
        } catch (err) {
            toast.error("Failed to fetch collection data");
        } finally {
            setLoading(false);
            fetchingRef.current = false;
        }
    }, []);

    useEffect(() => {
        fetchReports();
    }, [fetchReports]);

    const formatCurrency = (amount) => {
        if (!amount) return "$0.00";
        return new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: "USD",
        }).format(amount);
    };

    const getOverdueBadgeColor = (days) => {
        if (days === 0) return "#16a34a";
        if (days <= 30) return "#f59e0b";
        if (days <= 60) return "#f97316";
        if (days <= 90) return "#ef4444";
        return "#dc2626";
    };

    const getBucketColor = (bucket) => {
        switch (bucket) {
            case "1-30 Days": return "#f59e0b";
            case "31-60 Days": return "#f97316";
            case "61-90 Days": return "#ef4444";
            case "90+ Days": return "#dc2626";
            default: return "#16a34a";
        }
    };

    const handleStatCardClick = (filterType, filterValue = null) => {
        if (activeFilter && activeFilter.type === filterType && activeFilter.value === filterValue) {
            setActiveFilter(null);
        } else {
            setActiveFilter({ type: filterType, value: filterValue });
        }
    };

    const clearFilters = () => {
        setActiveFilter(null);
        setSearchTerm("");
    };

    const downloadSummaryPDF = async () => {
        try {
            const { default: autoTable } = await import('jspdf-autotable');
            const doc = new jsPDF('landscape'); // Changed to landscape for better fit

            // Title
            doc.setFontSize(22);
            doc.setTextColor(30, 41, 59);
            doc.text('Collection Dashboard - Summary Report', doc.internal.pageSize.width / 2, 20, { align: 'center' });

            // Subtitle
            doc.setFontSize(10);
            doc.setTextColor(100, 116, 139);
            doc.text(`Generated: ${moment().tz("Asia/Singapore").format('DD MMM YYYY, hh:mm A')} SGT`, doc.internal.pageSize.width / 2, 28, { align: 'center' });

            let yPosition = 40;

            // 1. Key Metrics Section
            doc.setFontSize(14);
            doc.setTextColor(30, 41, 59);
            doc.text('Key Performance Metrics', 15, yPosition);
            yPosition += 10;

            const keyMetricsData = [
                ['Total Customers', collectionStats.totalCustomers.toString()],
                ['Total Bill Amount', formatCurrency(financialStats.totalBillAmount)],
                ['Total Paid Amount', formatCurrency(financialStats.totalPaidAmount)],
                ['Total Pending Amount', formatCurrency(collectionStats.totalPendingAmount)],
                ['Total Overdue Amount', formatCurrency(collectionStats.totalOverdueAmount)],
                ['Total Monthly EMI', formatCurrency(financialStats.totalMonthlyEMI)],
                ["Today's EMI Due", formatCurrency(financialStats.todayMonthlyEMI)],
                ["Today's Paid EMI", formatCurrency(financialStats.todayPaidAmount)]
            ];

            autoTable(doc, {
                startY: yPosition,
                head: [['Metric', 'Value']],
                body: keyMetricsData,
                theme: 'grid',
                headStyles: {
                    fillColor: [59, 130, 246],
                    textColor: 255,
                    fontSize: 11,
                    fontStyle: 'bold'
                },
                bodyStyles: { fontSize: 10 },
                columnStyles: {
                    0: { fontStyle: 'bold', cellWidth: 120 },
                    1: { cellWidth: 80 }
                },
                margin: { left: 15, right: 15 }
            });

            yPosition = doc.lastAutoTable.finalY + 15;

            // 2. Overdue Analysis Section
            doc.setFontSize(14);
            doc.text('Overdue Bucket Analysis', 15, yPosition);
            yPosition += 10;

            const bucketData = [
                ['Current', collectionStats.overdueBuckets["Current"].count.toString(),
                    formatCurrency(collectionStats.overdueBuckets["Current"].amount),
                    `${((collectionStats.overdueBuckets["Current"].count / collectionStats.totalCustomers) * 100).toFixed(1)}%`],
                ['1-30 Days', collectionStats.overdueBuckets["1-30 Days"].count.toString(),
                    formatCurrency(collectionStats.overdueBuckets["1-30 Days"].amount),
                    `${((collectionStats.overdueBuckets["1-30 Days"].count / collectionStats.totalCustomers) * 100).toFixed(1)}%`],
                ['31-60 Days', collectionStats.overdueBuckets["31-60 Days"].count.toString(),
                    formatCurrency(collectionStats.overdueBuckets["31-60 Days"].amount),
                    `${((collectionStats.overdueBuckets["31-60 Days"].count / collectionStats.totalCustomers) * 100).toFixed(1)}%`],
                ['61-90 Days', collectionStats.overdueBuckets["61-90 Days"].count.toString(),
                    formatCurrency(collectionStats.overdueBuckets["61-90 Days"].amount),
                    `${((collectionStats.overdueBuckets["61-90 Days"].count / collectionStats.totalCustomers) * 100).toFixed(1)}%`],
                ['90+ Days', collectionStats.overdueBuckets["90+ Days"].count.toString(),
                    formatCurrency(collectionStats.overdueBuckets["90+ Days"].amount),
                    `${((collectionStats.overdueBuckets["90+ Days"].count / collectionStats.totalCustomers) * 100).toFixed(1)}%`]
            ];

            autoTable(doc, {
                startY: yPosition,
                head: [['Bucket', 'Customers', 'Amount', '% of Total']],
                body: bucketData,
                theme: 'grid',
                headStyles: {
                    fillColor: [239, 68, 68],
                    textColor: 255,
                    fontSize: 11,
                    fontStyle: 'bold'
                },
                bodyStyles: { fontSize: 10 },
                columnStyles: {
                    0: { fontStyle: 'bold', cellWidth: 60 },
                    1: { cellWidth: 50, halign: 'center' },
                    2: { cellWidth: 70 },
                    3: { cellWidth: 40, halign: 'center' }
                },
                margin: { left: 15, right: 15 }
            });

            yPosition = doc.lastAutoTable.finalY + 15;

            // 3. Today's Activity Section
            doc.setFontSize(14);
            doc.text("Today's Collection Activity", 15, yPosition);
            yPosition += 10;

            const todayData = [
                ['Due Today', financialStats.todayDueCustomers.toString(), formatCurrency(financialStats.todayMonthlyEMI)],
                ['Paid Today', financialStats.todayPaidCustomers.toString(), formatCurrency(financialStats.todayPaidAmount)],
                ['Total Today', (financialStats.todayDueCustomers + financialStats.todayPaidCustomers).toString(),
                    formatCurrency(financialStats.todayMonthlyEMI + financialStats.todayPaidAmount)]
            ];

            autoTable(doc, {
                startY: yPosition,
                head: [['Status', 'Customers', 'Amount']],
                body: todayData,
                theme: 'grid',
                headStyles: {
                    fillColor: [245, 158, 11],
                    textColor: 255,
                    fontSize: 11,
                    fontStyle: 'bold'
                },
                bodyStyles: { fontSize: 10 },
                columnStyles: {
                    0: { fontStyle: 'bold', cellWidth: 80 },
                    1: { cellWidth: 60, halign: 'center' },
                    2: { cellWidth: 70 }
                },
                margin: { left: 15, right: 15 }
            });

            // Add page number
            const pageCount = doc.getNumberOfPages();
            for (let i = 1; i <= pageCount; i++) {
                doc.setPage(i);
                doc.setFontSize(8);
                doc.setTextColor(100, 116, 139);
                doc.text(`Page ${i} of ${pageCount}`, doc.internal.pageSize.width - 30, doc.internal.pageSize.height - 10);
            }

            doc.save(`collection-summary-${moment().tz("Asia/Singapore").format('YYYY-MM-DD')}.pdf`);
            toast.success("Summary PDF downloaded successfully");
        } catch (error) {
            console.error('Error generating PDF:', error);
            toast.error("Failed to download summary report.");
        }
    };

    const downloadFilteredReportPDF = async () => {
        try {
            const { default: autoTable } = await import('jspdf-autotable');
            const doc = new jsPDF('landscape');

            doc.setFontSize(18);
            doc.setTextColor(30, 41, 59);
            doc.text('Collection Report', doc.internal.pageSize.width / 2, 15, { align: 'center' });

            doc.setFontSize(9);
            doc.setTextColor(100, 116, 139);

            let filterInfo = 'All Customers';
            if (activeFilter) {
                filterInfo = activeFilter.type === 'overdue-bucket'
                    ? activeFilter.value
                    : activeFilter.type.replace('-', ' ');
            }

            doc.text(`Generated: ${moment().tz("Asia/Singapore").format('DD MMM YYYY')} | Filter: ${filterInfo}`, doc.internal.pageSize.width / 2, 22, { align: 'center' });

            let yPosition = 30;

            const customerData = filteredReports.map((customer, index) => {
                const overdueDays = calculateOverdueDays(customer);
                const bucket = categorizeOverdueDays(overdueDays);
                const { monthlyEMI, pendingAmount } = calculateEMIDetails(customer);

                const currentMonth = moment().tz("Asia/Singapore").format("MMM YYYY");
                const currentMonthReport = customer.reports?.find(r => r.month === currentMonth);
                const currentStatus = currentMonthReport?.status || "—";

                const paidReports = customer.reports?.filter(r => r.status === "Paid") || [];
                const lastPayment = paidReports.sort((a, b) =>
                    moment(b.month, "MMM YYYY").diff(moment(a.month, "MMM YYYY"))
                )[0];

                const actionInfo = getActionRequiredInfo(customer);
                const statusStyle = getOverdueStatusStyle(overdueDays, bucket);

                return [
                    (index + 1).toString(),
                    customer.full_name || '—',
                    customer.phone_number || '—',
                    customer.boDDARefNo || '—',
                    currentStatus,
                    getDueDateDisplay(customer),
                    formatCurrency(monthlyEMI),
                    formatCurrency(pendingAmount),
                    bucket === "Current" ? "Current" : bucket.split(" ")[0],
                    actionInfo.action,
                    lastPayment ? lastPayment.month : '—'
                ];
            });

            autoTable(doc, {
                startY: yPosition,
                head: [['#', 'CUSTOMER', 'PHONE NUMBER', 'DDA REF', 'THIS MONTH', 'NEXT DD', 'MON.EMI', 'PENDING', 'STATUS', 'ACTION REQUIRED', 'LAST PAYMENT']],
                body: customerData,
                theme: 'striped',
                headStyles: {
                    fillColor: [59, 130, 246],
                    textColor: 255,
                    fontSize: 8,
                    fontStyle: 'bold',
                    halign: 'center'
                },
                bodyStyles: {
                    fontSize: 7,
                    cellPadding: 2,
                    overflow: 'linebreak',
                    lineWidth: 0.1
                },
                columnStyles: {
                    0: { cellWidth: 15, halign: 'center' },
                    1: { cellWidth: 40, halign: 'left' },
                    2: { cellWidth: 25, halign: 'left' },
                    3: { cellWidth: 30, halign: 'left' },
                    4: { cellWidth: 25, halign: 'center' },
                    5: { cellWidth: 25, halign: 'center' },
                    6: { cellWidth: 25, halign: 'right' },
                    7: { cellWidth: 25, halign: 'right' },
                    8: { cellWidth: 20, halign: 'center' },
                    9: { cellWidth: 25, halign: 'left' },
                    10: { cellWidth: 25, halign: 'center' }
                },
                margin: { left: 5, right: 5 },
                pageBreak: 'auto',
                styles: {
                    fontSize: 7,
                    cellPadding: 1
                },
                didDrawPage: function (data) {
                    doc.setFontSize(8);
                    doc.setTextColor(100, 116, 139);
                    doc.text(`Page ${data.pageNumber}`, doc.internal.pageSize.width - 15, 10);
                }
            });

            const finalY = doc.lastAutoTable.finalY + 10;

            if (finalY < doc.internal.pageSize.height - 20) {
                doc.setFontSize(9);
                doc.setTextColor(30, 41, 59);
                doc.text(`Total Records: ${filteredReports.length}`, 15, finalY);
            }

            doc.save(`collection-report-${moment().tz("Asia/Singapore").format('YYYY-MM-DD')}.pdf`);
            toast.success(`PDF downloaded with ${filteredReports.length} records`);
        } catch (error) {
            console.error('Error generating PDF:', error);
            toast.error("Failed to download report");
        }
    };

    const getDueDateDisplay = (row) => {
        if (!row.payment_date) return "—";

        const baseDate = moment(row.payment_date).tz("Asia/Singapore");
        const currentMonth = moment().tz("Asia/Singapore").format("MMM YYYY");
        const currentMonthReport = row.reports?.find(r => r.month === currentMonth);
        const currentStatus = currentMonthReport?.status || "Not Started";

        let dueDate;
        if (currentStatus === "Paid") {
            dueDate = moment().tz("Asia/Singapore")
                .add(1, 'month')
                .startOf('month');
        } else {
            dueDate = moment().tz("Asia/Singapore")
                .startOf('month');
        }

        const originalDay = baseDate.date();
        const lastDayOfMonth = dueDate.clone().endOf('month').date();
        const actualDueDay = originalDay > lastDayOfMonth ? lastDayOfMonth : originalDay;

        dueDate = dueDate.date(actualDueDay).startOf('day');

        const day = dueDate.date();
        const getOrdinalSuffix = (d) => {
            if (d > 3 && d < 21) return 'th';
            switch (d % 10) {
                case 1: return 'st';
                case 2: return 'nd';
                case 3: return 'rd';
                default: return 'th';
            }
        };

        const ordinalDay = `${day}${getOrdinalSuffix(day)}`;
        const month = dueDate.format("MMM");
        return `${ordinalDay} ${month}`;
    };

    const getThisMonthStatusCell = (row) => {
        const currentMonth = moment().tz("Asia/Singapore").format("MMM YYYY");
        const currentMonthReport = row.reports?.find(r => r.month === currentMonth);
        const status = currentMonthReport?.status || "Not Started";

        const getStatusColor = (status) => {
            switch (status) {
                case "Paid": return { bg: "#dcfce7", text: "#166534", border: "#16a34a" };
                case "Pending": return { bg: "#fef9c3", text: "#854d0e", border: "#eab308" };
                case "Unpaid": return { bg: "#fee2e2", text: "#991b1b", border: "#ef4444" };
                default: return { bg: "#f3f4f6", text: "#4b5563", border: "#9ca3af" };
            }
        };

        const colors = getStatusColor(status);
        let statusText, tooltipText;

        switch (status) {
            case "Paid":
                statusText = "✓ Cleared";
                tooltipText = `Paid on ${currentMonthReport?.comments || currentMonth}`;
                break;
            case "Pending":
                statusText = "⏳ Pending";
                tooltipText = `Due for ${currentMonth}`;
                break;
            case "Unpaid":
                statusText = "⚠️ Overdue";
                tooltipText = `Overdue for ${currentMonth}`;
                break;
            default:
                statusText = "—";
                tooltipText = `No payment scheduled for ${currentMonth}`;
                break;
        }

        return (
            <OverlayTrigger
                placement="top"
                overlay={<Tooltip>{tooltipText}</Tooltip>}
            >
                <span
                    className="d-inline-flex align-items-center justify-content-center px-2 py-1 rounded"
                    style={{
                        backgroundColor: colors.bg,
                        color: colors.text,
                        border: `1px solid ${colors.border}`,
                        fontSize: "0.7rem",
                        fontWeight: "500",
                        minWidth: "70px",
                        cursor: "pointer"
                    }}
                >
                    {statusText}
                </span>
            </OverlayTrigger>
        );
    };

    const getDueDateSelector = (row) => {
        if (!row.payment_date) return 0;
        const baseDate = moment(row.payment_date).tz("Asia/Singapore");
        return baseDate.date();
    };

    const getDueDateCell = (row) => {
        if (!row.payment_date) return "—";

        const baseDate = moment(row.payment_date).tz("Asia/Singapore");
        const currentMonth = moment().tz("Asia/Singapore").format("MMM YYYY");
        const currentMonthReport = row.reports?.find(r => r.month === currentMonth);
        const currentStatus = currentMonthReport?.status || "Not Started";
        let dueDate;
        let isNextMonth = false;
        let wasDateAdjusted = false;

        if (currentStatus === "Paid") {
            dueDate = moment().tz("Asia/Singapore")
                .add(1, 'month')
                .startOf('month');
            isNextMonth = true;
        } else {
            dueDate = moment().tz("Asia/Singapore")
                .startOf('month');
        }
        const originalDay = baseDate.date();
        const lastDayOfMonth = dueDate.clone().endOf('month').date();

        let actualDueDay;
        if (originalDay > lastDayOfMonth) {
            actualDueDay = lastDayOfMonth;
            wasDateAdjusted = true;
        } else {
            actualDueDay = originalDay;
        }

        dueDate = dueDate.date(actualDueDay).startOf('day');

        const today = moment().tz("Asia/Singapore").startOf('day');
        const isOverdue = today.isAfter(dueDate, 'day');
        const isToday = today.isSame(dueDate, 'day');

        const getOrdinalSuffix = (d) => {
            if (d > 3 && d < 21) return 'th';
            switch (d % 10) {
                case 1: return 'st';
                case 2: return 'nd';
                case 3: return 'rd';
                default: return 'th';
            }
        };

        const day = dueDate.date();
        const ordinalDay = `${day}${getOrdinalSuffix(day)}`;
        const month = dueDate.format("MMM");
        const displayDate = wasDateAdjusted ? `~${ordinalDay} ${month}` : `${ordinalDay} ${month}`;

        let bgColor, textColor, borderColor, icon, tooltipText;

        if (currentStatus === "Paid") {
            bgColor = "#f0fdf4";
            textColor = "#166534";
            borderColor = "#86efac";
            icon = "✓";
            tooltipText = `✓ Paid for ${currentMonth}. Next EMI due on ${dueDate.format("DD MMM YYYY")}`;
        } else if (isOverdue) {
            bgColor = "#fef2f2";
            textColor = "#991b1b";
            borderColor = "#fca5a5";
            icon = "⚠️";
            tooltipText = `⚠️ OVERDUE! Payment was due on ${dueDate.format("DD MMM YYYY")}`;
        } else if (isToday) {
            bgColor = "#fef9c3";
            textColor = "#854d0e";
            borderColor = "#fde047";
            icon = "📅";
            tooltipText = `📅 TODAY! EMI will be auto-deducted`;
        } else if (isNextMonth) {
            bgColor = "#eff6ff";
            textColor = "#1e40af";
            borderColor = "#93c5fd";
            icon = "→";
            tooltipText = `→ Next EMI due on ${dueDate.format("DD MMM YYYY")}`;
        } else {
            bgColor = "#f9fafb";
            textColor = "#4b5563";
            borderColor = "#d1d5db";
            icon = "";
            tooltipText = `EMI due on ${dueDate.format("DD MMM YYYY")}`;
        }

        if (wasDateAdjusted) {
            tooltipText += `\n(Adjusted from ${originalDay}${getOrdinalSuffix(originalDay)} - ${dueDate.format("MMMM")} has only ${lastDayOfMonth} days)`;
        }

        return (
            <OverlayTrigger
                placement="top"
                overlay={<Tooltip>{tooltipText}</Tooltip>}
            >
                <div
                    className="d-flex align-items-center gap-1 p-1 rounded"
                    style={{
                        backgroundColor: bgColor,
                        color: textColor,
                        border: `1px solid ${borderColor}`,
                        fontSize: "0.7rem",
                        fontWeight: "500",
                        minWidth: "70px",
                        cursor: "pointer"
                    }}
                >
                    {icon && <span className="me-1">{icon}</span>}
                    <div className="d-flex flex-column">
                        <div className="fw-bold">{displayDate}</div>
                        {wasDateAdjusted && (
                            <div className="small" style={{ fontSize: "0.6rem", opacity: 0.8 }}>adj.</div>
                        )}
                    </div>
                </div>
            </OverlayTrigger>
        );
    };

    const getOverdueStatusStyle = (overdueDays, bucket) => {
        const color = getOverdueBadgeColor(overdueDays);

        const bgColors = {
            "#16a34a": "#f0fdf4",
            "#f59e0b": "#fef9c3",
            "#f97316": "#ffedd5",
            "#ef4444": "#fee2e2",
            "#dc2626": "#fecaca"
        };

        return {
            color: color,
            bgColor: bgColors[color] || "#f3f4f6",
            label: bucket === "Current" ? "Current" : `${overdueDays}d`
        };
    };

    const renderSimpleStatusCell = (row) => {
        const overdueDays = calculateOverdueDays(row);
        const bucket = categorizeOverdueDays(overdueDays);
        const style = getOverdueStatusStyle(overdueDays, bucket);

        return (
            <div className="d-flex align-items-center">
                <div style={{
                    width: "6px",
                    height: "6px",
                    backgroundColor: style.color,
                    borderRadius: "50%",
                    marginRight: "6px"
                }} />
                <span style={{
                    fontSize: "0.75rem",
                    fontWeight: "500",
                    color: style.color
                }}>
                    {bucket === "Current" ? "Current" : bucket.split(" ")[0]}
                </span>
                {overdueDays > 0 && (
                    <span style={{
                        fontSize: "0.7rem",
                        fontWeight: "400",
                        color: "#6b7280",
                        marginLeft: "4px"
                    }}>
                        ({overdueDays}d)
                    </span>
                )}
            </div>
        );
    };

    const getActionRequiredInfo = (row) => {
        const overdueDays = calculateOverdueDays(row);
        const currentMonth = moment().tz("Asia/Singapore").format("MMM YYYY");
        const currentMonthReport = row.reports?.find(r => r.month === currentMonth);
        const currentStatus = currentMonthReport?.status || "—";

        if (currentStatus === "Paid") {
            return {
                priority: "Completed",
                color: "#16a34a",
                action: "No action needed",
                icon: <FiCheckCircle size={12} />
            };
        }

        if (currentStatus === "Pending") {
            return {
                priority: "Pending",
                color: "#f59e0b",
                action: "Awaiting payment",
                icon: <FiClock size={12} />
            };
        }

        if (currentStatus === "Unpaid") {
            if (overdueDays <= 15) {
                return {
                    priority: "Overdue",
                    color: "#dc2626",
                    action: "Call customer",
                    icon: <FiPhone size={12} />
                };
            }
            if (overdueDays <= 30) {
                return {
                    priority: "Overdue",
                    color: "#991b1b",
                    action: "Send reminder",
                    icon: <FiAlertCircle size={12} />
                };
            }
            if (overdueDays <= 60) {
                return {
                    priority: "Critical",
                    color: "#7f1d1d",
                    action: "Escalate",
                    icon: <FiAlertTriangle size={12} />
                };
            }
            return {
                priority: "Critical",
                color: "#450a0a",
                action: "Legal notice",
                icon: <FiAlertOctagon size={12} />
            };
        }

        return {
            priority: "Monitor",
            color: "#6b7280",
            action: "Check status",
            icon: <FiEye size={12} />
        };
    };

    const renderActionRequiredCell = (row) => {
        const info = getActionRequiredInfo(row);

        return (
            <div className="d-flex align-items-center gap-2 px-2 py-1">
                <div style={{ color: info.color }}>
                    {info.icon}
                </div>
                <div className="d-flex flex-column">
                    <span style={{
                        fontSize: "0.7rem",
                        fontWeight: "600",
                        color: info.color
                    }}>
                        {info.priority}
                    </span>
                    <span style={{
                        fontSize: "0.65rem",
                        color: "#4b5563",
                        lineHeight: "1.2"
                    }}>
                        {info.action}
                    </span>
                </div>
            </div>
        );
    };

    const getActionRequiredSelector = (row) => {
        return calculateOverdueDays(row);
    };

    const columns = [
        {
            name: "#",
            selector: (row, index) => index + 1,
            width: "60px",
            sortable: true
        },
        {
            name: "CUSTOMER",
            selector: (row) => row.full_name,
            sortable: true,
            width: "160px",
            cell: (row) => (
                <div className="text-truncate" style={{ maxWidth: "150px" }} title={row.full_name}>
                    {row.full_name || "—"}
                </div>
            )
        },
        {
            name: "PHONE NUMBER",
            cell: (row) => (
                <div className="d-flex align-items-center gap-1">
                    <FiPhone size={14} className="text-muted" />
                    <span className="font-semibold text-gray-600 small">
                        {row.phone_number || "—"}
                    </span>
                </div>
            ),
            width: "160px",
            sortable: true,
        },
        {
            name: "DDA REF",
            cell: (row) => (
                <span className="font-semibold text-gray-600 small">
                    {row.boDDARefNo || "—"}
                </span>
            ),
            width: "120px",
            sortable: false,
        },
        {
            name: "THIS MONTH",
            width: "130px",
            sortable: true,
            selector: (row) => {
                const currentMonth = moment().tz("Asia/Singapore").format("MMM YYYY");
                const currentMonthReport = row.reports?.find(r => r.month === currentMonth);
                return currentMonthReport?.status || "Not Started";
            },
            cell: (row) => getThisMonthStatusCell(row)
        },
        {
            name: "Next DD",
            width: "120px",
            sortable: true,
            selector: getDueDateSelector,
            cell: (row) => getDueDateCell(row)
        },
        {
            name: "Mon.EMI",
            cell: (row) => {
                const { monthlyEMI } = calculateEMIDetails(row);
                return (
                    <span style={{ fontWeight: 600 }} className="text-primary font-semibold">
                        {formatCurrency(monthlyEMI)}
                    </span>
                );
            },
            width: "100px",
            sortable: true,
        },
        {
            name: "PENDING",
            cell: (row) => {
                const { pendingAmount } = calculateEMIDetails(row);
                return (
                    <span className="text-red-600 font-semibold">
                        {formatCurrency(pendingAmount)}
                    </span>
                );
            },
            width: "110px",
            sortable: true,
        },
        {
            name: "STATUS",
            selector: (row) => calculateOverdueDays(row),
            sortable: true,
            cell: renderSimpleStatusCell,
            width: "110px",
        },
        {
            name: "ACTION REQUIRED",
            selector: getActionRequiredSelector,
            sortable: true,
            cell: renderActionRequiredCell,
            width: "180px",
        },
        {
            name: "LAST PAYMENT",
            cell: (row) => {
                const paidReports = row.reports?.filter(r => r.status === "Paid") || [];
                const lastPayment = paidReports.sort((a, b) =>
                    moment(b.month, "MMM YYYY").tz("Asia/Singapore").diff(moment(a.month, "MMM YYYY").tz("Asia/Singapore"))
                )[0];

                return (
                    <span className="text-muted small">
                        {lastPayment ? lastPayment.month : "—"}
                    </span>
                );
            },
            width: "150px",
        }
    ];

    const StatCard = ({ title, value, subtitle, icon, color, filterType, filterValue, isActive }) => {
        const hexToRgba = (hex, alpha) => {
            const r = parseInt(hex.slice(1, 3), 16);
            const g = parseInt(hex.slice(3, 5), 16);
            const b = parseInt(hex.slice(5, 7), 16);
            return `rgba(${r}, ${g}, ${b}, ${alpha})`;
        };

        return (
            <Card
                className={`border-0 shadow-sm h-100 ${isActive ? 'active-stat-card' : ''}`}
                style={{
                    cursor: 'pointer',
                    border: isActive ? `2px solid ${color}` : '1px solid #e5e7eb',
                    backgroundColor: isActive ? hexToRgba(color, 0.08) : '#ffffff',
                    transform: isActive ? 'translateY(-2px)' : 'none',
                    boxShadow: isActive ? `0 4px 12px ${hexToRgba(color, 0.15)}` : '0 1px 3px rgba(0,0,0,0.1)',
                    transition: 'all 0.3s ease'
                }}
                onClick={() => handleStatCardClick(filterType, filterValue)}
            >
                <Card.Body className="p-3">
                    <div className="d-flex align-items-center">
                        <div
                            className="rounded-circle d-flex align-items-center justify-content-center me-3"
                            style={{
                                width: "48px",
                                height: "48px",
                                backgroundColor: isActive ? color : `${color}15`,
                                color: isActive ? '#fff' : color,
                                transition: 'all 0.3s ease'
                            }}
                        >
                            {React.cloneElement(icon, {
                                size: 20,
                                color: isActive ? '#fff' : color
                            })}
                        </div>
                        <div>
                            <h6 className="mb-1" style={{
                                fontSize: "0.875rem",
                                color: isActive ? color : '#6b7280',
                                fontWeight: isActive ? '600' : '400'
                            }}>
                                {title}
                            </h6>
                            <h4 className="fw-bold mb-0" style={{
                                color: isActive ? color : '#1f2937',
                                fontSize: "1.5rem"
                            }}>
                                {value}
                            </h4>
                            {subtitle && (
                                <small style={{
                                    fontSize: "0.75rem",
                                    color: isActive ? color : '#6b7280'
                                }}>
                                    {subtitle}
                                </small>
                            )}
                        </div>
                    </div>
                </Card.Body>
            </Card>
        );
    };

    return (
        <div className="container-fluid py-2">
            <div className="row mb-4">
                <div className="col-12">
                    <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3">
                        <div className="flex-grow-1">
                            <div className="d-flex flex-column flex-lg-row align-items-start align-items-lg-center gap-3 mb-2">
                                <h5 className="fw-bold mb-0" style={{ fontSize: "16px" }}>
                                    Monitor overdue payments and collection buckets
                                </h5>

                                {activeFilter && (
                                    <div className="d-flex align-items-center">
                                        <Badge bg="primary" className="px-2 py-1 d-flex align-items-center gap-1">
                                            <span className="text-nowrap">
                                                Filter: {activeFilter.type === 'overdue-bucket'
                                                    ? activeFilter.value
                                                    : activeFilter.type.replace('-', ' ')}
                                            </span>
                                            <Button
                                                variant="link"
                                                className="p-0 text-white lh-1 fs-6"
                                                onClick={clearFilters}
                                            >
                                                <FiX size={12} />
                                            </Button>
                                        </Badge>
                                    </div>
                                )}
                            </div>

                            <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                                <div
                                    className="d-flex align-items-center gap-1 px-2 py-1 rounded border border-success"
                                    style={{ backgroundColor: '#dcfce7' }}
                                >
                                    <div
                                        className="rounded-circle"
                                        style={{ width: '6px', height: '6px', backgroundColor: '#16a34a' }}
                                    />
                                    <span className="fw-semibold" style={{ fontSize: '0.75rem', color: '#166534' }}>
                                        {collectionStats.overdueBuckets["Current"].count} Current
                                    </span>
                                </div>

                                <OverlayTrigger
                                    placement="bottom"
                                    overlay={
                                        <Tooltip>
                                            <div className="text-start">
                                                <strong>Overdue Customers ({reportsData.filter(r => calculateOverdueDays(r) > 0).length})</strong>
                                                <div className="mt-2" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                                                    {reportsData
                                                        .filter(r => calculateOverdueDays(r) > 0)
                                                        .sort((a, b) => calculateOverdueDays(b) - calculateOverdueDays(a))
                                                        .map(customer => (
                                                            <div key={customer._id} className="small py-1">
                                                                • {customer.full_name || 'Unknown Customer'}
                                                                <span className="text-danger ms-1">
                                                                    ({calculateOverdueDays(customer)} days)
                                                                </span>
                                                            </div>
                                                        ))
                                                    }
                                                    {reportsData.filter(r => calculateOverdueDays(r) > 0).length === 0 && (
                                                        <div className="small text-muted">No overdue customers</div>
                                                    )}
                                                </div>
                                            </div>
                                        </Tooltip>
                                    }
                                >
                                    <div
                                        className="d-flex align-items-center gap-1 px-2 py-1 rounded border border-danger"
                                        style={{ backgroundColor: '#fef2f2' }}
                                    >
                                        <div
                                            className="rounded-circle"
                                            style={{ width: '6px', height: '6px', backgroundColor: '#dc2626' }}
                                        />
                                        <span className="fw-semibold" style={{ fontSize: '0.75rem', color: '#991b1b' }}>
                                            {reportsData.filter(r => calculateOverdueDays(r) > 0).length} Overdue
                                        </span>
                                    </div>
                                </OverlayTrigger>
                            </div>

                            <div className="d-flex flex-wrap align-items-center gap-2">
                                <span className="text-muted small text-nowrap">Overdue breakdown:</span>
                                <div className="d-flex flex-wrap align-items-center gap-1">
                                    {Object.entries(collectionStats.overdueBuckets)
                                        .filter(([bucket]) => bucket !== "Current")
                                        .map(([bucket, data], index, array) => (
                                            <OverlayTrigger
                                                key={bucket}
                                                placement="bottom"
                                                overlay={
                                                    <Tooltip>
                                                        <div className="text-start">
                                                            <strong>{bucket} Overdue ({data.count})</strong>
                                                            <div className="mt-2" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                                                                {reportsData
                                                                    .filter(r => {
                                                                        const days = calculateOverdueDays(r);
                                                                        const customerBucket = categorizeOverdueDays(days);
                                                                        return customerBucket === bucket;
                                                                    })
                                                                    .sort((a, b) => calculateOverdueDays(b) - calculateOverdueDays(a))
                                                                    .map(customer => (
                                                                        <div key={customer._id} className="small py-1">
                                                                            • {customer.full_name || 'Unknown Customer'}
                                                                            <span style={{ color: getBucketColor(bucket), marginLeft: '8px' }}>
                                                                                ({calculateOverdueDays(customer)} days)
                                                                            </span>
                                                                        </div>
                                                                    ))
                                                                }
                                                                {data.count === 0 && (
                                                                    <div className="small text-muted">No customers in this bucket</div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </Tooltip>
                                                }
                                            >
                                                <div className="d-flex align-items-center">
                                                    <div
                                                        className="rounded-circle me-1"
                                                        style={{
                                                            width: '8px',
                                                            height: '8px',
                                                            backgroundColor: getBucketColor(bucket)
                                                        }}
                                                    />
                                                    <span
                                                        className="small fw-medium text-nowrap"
                                                        style={{ color: getBucketColor(bucket) }}
                                                    >
                                                        {data.count}
                                                    </span>
                                                    {index < array.length - 1 && (
                                                        <span className="text-muted small mx-1">•</span>
                                                    )}
                                                </div>
                                            </OverlayTrigger>
                                        ))
                                    }
                                </div>
                            </div>
                        </div>

                        <div className="d-flex flex-wrap gap-2 justify-content-start justify-content-md-end">
                            <Dropdown>
                                <Dropdown.Toggle
                                    variant="outline-primary"
                                    size="sm"
                                    className="d-flex align-items-center gap-2 border"
                                    style={{ fontSize: '0.75rem' }}
                                >
                                    <FiDownload size={14} />
                                    <span className="d-none d-sm-inline">Export Reports</span>
                                </Dropdown.Toggle>

                                <Dropdown.Menu>
                                    <Dropdown.Item onClick={downloadSummaryPDF} className="py-2">
                                        <div className="d-flex align-items-center gap-2">
                                            <FiTrendingUp size={14} className="text-primary" />
                                            <div className="text-start" style={{ fontSize: '0.8rem' }}>
                                                <div className="fw-medium">Summary Dashboard</div>
                                                <small className="text-muted">Key metrics, overdue buckets & today's activity</small>
                                            </div>
                                        </div>
                                    </Dropdown.Item>
                                    <Dropdown.Item onClick={downloadFilteredReportPDF} className="py-2">
                                        <div className="d-flex align-items-center gap-2">
                                            <FiUsers size={14} className="text-primary" />
                                            <div className="text-start" style={{ fontSize: '0.8rem' }}>
                                                <div className="fw-medium">Table Data Export</div>
                                                <small className="text-muted">Exact table data as shown ({filteredReports.length} rows)</small>
                                            </div>
                                        </div>
                                    </Dropdown.Item>
                                </Dropdown.Menu>
                            </Dropdown>

                            <div className="d-flex gap-2">
                                <Button
                                    variant="outline-primary"
                                    size="sm"
                                    className="d-flex align-items-center gap-2 border"
                                    style={{ fontSize: '0.75rem' }}
                                    onClick={() => window.location.href = '/reports'}
                                >
                                    <FiCreditCard size={14} />
                                    <span className="d-none d-sm-inline">Reports</span>
                                </Button>

                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    className="d-flex align-items-center gap-2 border"
                                    style={{ fontSize: '0.75rem' }}
                                    onClick={fetchReports}
                                    disabled={loading}
                                >
                                    <FiRefreshCw size={14} className={loading ? "spinning" : ""} />
                                    <span className="d-none d-sm-inline">Refresh</span>
                                </Button>

                                {activeFilter && (
                                    <Button
                                        variant="outline-danger"
                                        size="sm"
                                        className="d-flex align-items-center gap-2 border"
                                        style={{ fontSize: '0.75rem' }}
                                        onClick={clearFilters}
                                    >
                                        <span className="d-none d-sm-inline">Clear Filter</span>
                                    </Button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <Row className="mb-4 g-3">
                <Col md={3}>
                    <StatCard
                        title="Total Customers"
                        value={collectionStats.totalCustomers}
                        icon={<FiUsers />}
                        color="#374151"
                        filterType="all"
                        isActive={activeFilter?.type === 'all'}
                    />
                </Col>
                <Col md={3}>
                    <StatCard
                        title="Actively Paying"
                        value={collectionStats.overdueBuckets["Current"].count}
                        subtitle={formatCurrency(collectionStats.overdueBuckets["Current"].amount)}
                        icon={<FiCheckCircle />}
                        color="#16a34a"
                        filterType="current"
                        isActive={activeFilter?.type === 'current'}
                    />
                </Col>
                <Col md={3}>
                    <StatCard
                        title="Total Overdue Amount"
                        value={formatCurrency(collectionStats.totalOverdueAmount)}
                        subtitle={`${reportsData.filter(r => calculateOverdueDays(r) > 0).length} customers`}
                        icon={<FiAlertCircle />}
                        color="#dc2626"
                        filterType="overdue"
                        isActive={activeFilter?.type === 'overdue'}
                    />
                </Col>

                <Col md={3}>
                    <StatCard
                        title={`Today's EMI Due (${moment().tz("Asia/Singapore").format('DD MMM')})`}
                        value={formatCurrency(financialStats.todayMonthlyEMI)}
                        subtitle={
                            <div className="small">
                                <div className="d-flex justify-content-between">
                                    <span>Due: {formatCurrency(financialStats.todayMonthlyEMI)}</span>
                                    <span className="fw-bold">Paid: {formatCurrency(financialStats.todayPaidAmount)}</span>
                                </div>
                                <div className="d-flex justify-content-between mt-1">
                                    <span>{financialStats.todayDueCustomers} customers</span>
                                    <span className="fw-bold">{financialStats.todayPaidCustomers} paid</span>
                                </div>
                                <div className="progress mt-1" style={{ height: '4px' }}>
                                    <div
                                        className="progress-bar"
                                        style={{
                                            backgroundColor: '#16a34a',
                                            width: `${financialStats.todayPaidAmount === 0 ? 0 : (financialStats.todayPaidAmount / (financialStats.todayMonthlyEMI + financialStats.todayPaidAmount)) * 100}%`
                                        }}
                                    />
                                </div>
                            </div>
                        }
                        icon={<FiCalendar />}
                        color="#d97706"
                        filterType="today-due"
                        isActive={activeFilter?.type === 'today-due'}
                    />
                </Col>
            </Row>

            <Row className="mb-4 g-3">
                <Col md={3}>
                    <StatCard
                        title="Total Monthly EMI"
                        value={formatCurrency(financialStats.totalMonthlyEMI)}
                        icon={<FiTrendingUp />}
                        color="#3b82f6"
                        filterType="all"
                        isActive={activeFilter?.type === 'all'}
                    />
                </Col>
                <Col md={3}>
                    <StatCard
                        title="Total Paid Amount"
                        value={formatCurrency(financialStats.totalPaidAmount)}
                        icon={<FiCheckCircle />}
                        color="#16a34a"
                        filterType="paid-amount"
                        isActive={activeFilter?.type === 'paid-amount'}
                    />
                </Col>
                <Col md={3}>
                    <StatCard
                        title="Total Pending Amount"
                        value={formatCurrency(collectionStats.totalPendingAmount)}
                        icon={<FiClock />}
                        color="#f59e0b"
                        filterType="pending-amount"
                        isActive={activeFilter?.type === 'pending-amount'}
                    />
                </Col>
                <Col md={3}>
                    <StatCard
                        title="Total Bill Amount"
                        value={formatCurrency(financialStats.totalBillAmount)}
                        icon={<FiCreditCard />}
                        color="#8b5cf6"
                        filterType="all"
                        isActive={activeFilter?.type === 'all'}
                    />
                </Col>
            </Row>

            <Row className="mb-4 g-3">
                <Col md={3}>
                    <StatCard
                        title="1-30 Days Overdue"
                        value={collectionStats.overdueBuckets["1-30 Days"].count}
                        subtitle={formatCurrency(collectionStats.overdueBuckets["1-30 Days"].amount)}
                        icon={<FiClock />}
                        color="#f59e0b"
                        filterType="overdue-bucket"
                        filterValue="1-30 Days"
                        isActive={activeFilter?.type === 'overdue-bucket' && activeFilter?.value === "1-30 Days"}
                    />
                </Col>
                <Col md={3}>
                    <StatCard
                        title="31-60 Days Overdue"
                        value={collectionStats.overdueBuckets["31-60 Days"].count}
                        subtitle={formatCurrency(collectionStats.overdueBuckets["31-60 Days"].amount)}
                        icon={<FiAlertCircle />}
                        color="#f97316"
                        filterType="overdue-bucket"
                        filterValue="31-60 Days"
                        isActive={activeFilter?.type === 'overdue-bucket' && activeFilter?.value === "31-60 Days"}
                    />
                </Col>
                <Col md={3}>
                    <StatCard
                        title="61-90 Days Overdue"
                        value={collectionStats.overdueBuckets["61-90 Days"].count}
                        subtitle={formatCurrency(collectionStats.overdueBuckets["61-90 Days"].amount)}
                        icon={<FiAlertCircle />}
                        color="#ef4444"
                        filterType="overdue-bucket"
                        filterValue="61-90 Days"
                        isActive={activeFilter?.type === 'overdue-bucket' && activeFilter?.value === "61-90 Days"}
                    />
                </Col>
                <Col md={3}>
                    <StatCard
                        title="90+ Days Overdue"
                        value={collectionStats.overdueBuckets["90+ Days"].count}
                        subtitle={formatCurrency(collectionStats.overdueBuckets["90+ Days"].amount)}
                        icon={<FiAlertCircle />}
                        color="#dc2626"
                        filterType="overdue-bucket"
                        filterValue="90+ Days"
                        isActive={activeFilter?.type === 'overdue-bucket' && activeFilter?.value === "90+ Days"}
                    />
                </Col>
            </Row>

            <Card className="border-0 shadow-sm">
                <Card.Body className="p-0">
                    <div className="p-3 border-bottom bg-gray-50">
                        <div className="row g-2 align-items-center">
                            <div className="col-md-6">
                                <div className="input-group" style={{ maxWidth: "400px" }}>
                                    <span className="input-group-text bg-white border-end-0">
                                        <FiSearch size={16} className="text-gray-500" />
                                    </span>
                                    <input
                                        type="text"
                                        className="form-control border-start-0"
                                        placeholder="Search customers, products, DDA references..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        style={{ fontSize: "0.875rem" }}
                                    />
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div className="d-flex justify-content-end gap-2">
                                    <Badge bg="danger" className="px-2 py-1">
                                        {filteredReports.filter(r => calculateOverdueDays(r) > 0).length} Overdue
                                    </Badge>
                                    <Badge bg="success" className="px-2 py-1">
                                        {filteredReports.filter(r => calculateOverdueDays(r) === 0).length} Current
                                    </Badge>
                                    {activeFilter && (
                                        <Badge bg="primary" className="px-2 py-1">
                                            Filter: {activeFilter.type === 'overdue-bucket' ? activeFilter.value : activeFilter.type.replace('-', ' ')}
                                        </Badge>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="p-3">
                        {loading ? (
                            <div className="d-flex justify-content-center align-items-center py-5">
                                <Spinner animation="border" variant="secondary" />
                                <span className="ms-2 text-muted">Loading collection data...</span>
                            </div>
                        ) : (
                            <DataTable
                                columns={columns}
                                data={filteredReports}
                                pagination
                                paginationPerPage={10}
                                paginationRowsPerPageOptions={[5, 10, 15, 20]}
                                highlightOnHover
                                responsive
                                striped
                                persistTableHead
                                fixedHeader
                                fixedHeaderScrollHeight="calc(100vh - 400px)"
                                customStyles={{
                                    headCells: {
                                        style: {
                                            backgroundColor: "#f9fafb",
                                            fontWeight: "600",
                                            fontSize: "0.75rem",
                                            textTransform: "uppercase",
                                            letterSpacing: "0.5px",
                                            color: "#374151",
                                            borderBottom: "1px solid #e5e7eb"
                                        },
                                    },
                                    cells: {
                                        style: {
                                            fontSize: "0.875rem",
                                            paddingTop: "12px",
                                            paddingBottom: "12px",
                                            color: "#374151"
                                        }
                                    }
                                }}
                            />
                        )}
                    </div>
                </Card.Body>
            </Card>

            <ToastContainer
                position="top-right"
                autoClose={2000}
                newestOnTop
                theme="light"
            />
        </div>
    );
};

export default Collection;